import {
  Building2Icon,
  ChartPieIcon,
  HandshakeIcon,
  LandmarkIcon,
  ShieldCheckIcon,
  SmartphoneIcon,
} from "zudoku/icons";

// Landing page content. Every product is part of the Finarkein Platform,
// grouped into finarkein.com's four layers; keep this in step with the
// "Platform" sidebar in zudoku.config.tsx.
export const CATEGORIES = [
  { id: "collection", label: "Data collection" },
  { id: "intelligence", label: "Data intelligence" },
  { id: "decision", label: "Decision intelligence" },
  { id: "actions", label: "Autonomous actions" },
] as const;

type CategoryId = (typeof CATEGORIES)[number]["id"];
type Tone = "forest" | "teal" | "slate" | "olive";

const TONE: Record<CategoryId, Tone> = {
  collection: "forest",
  intelligence: "teal",
  decision: "slate",
  actions: "olive",
};

const product = (p: {
  name: string;
  to: string;
  category: CategoryId;
  summary: string;
  available: boolean;
}) => ({ ...p, tone: TONE[p.category] });

export const PRODUCTS = [
  product({
    name: "Multi-AA Gateway",
    to: "/mag/overview",
    category: "collection",
    summary:
      "One integration to collect consented financial data across Account Aggregators, with health-based routing and fallback.",
    available: true,
  }),
  product({
    name: "FIP Gateway",
    to: "/fip-gateway/overview",
    category: "collection",
    summary:
      "Meet every Account Aggregator obligation as a data provider: discovery, linking, consent and data, with audit and reports.",
    available: true,
  }),
  product({
    name: "Doc Parser",
    to: "/doc-parser/overview",
    category: "collection",
    summary:
      "Turn a bank statement file into structured data in the Account Aggregator format, with tamper checks.",
    available: true,
  }),
  product({
    name: "Digital ITR",
    to: "/digital-itr/overview",
    category: "collection",
    summary:
      "Fetch a customer's income tax data from the Income Tax portal, with consent, as verified structured data.",
    available: true,
  }),
  product({
    name: "Portfos",
    to: "/portfos/overview",
    category: "intelligence",
    summary:
      "A white-label personal finance engine: net worth, spends, investments and nudges inside your own app.",
    available: true,
  }),
  product({
    name: "OpenLink",
    to: "/openlink/overview",
    category: "actions",
    summary:
      "An embedded-finance network: platforms and financial providers integrate once and reach each other.",
    available: true,
  }),
];

export const QUICK_LINKS = [
  { label: "Finarkein Platform overview", to: "/platform" },
  { label: "LSP integration guide", to: "/mag/lsp/integration-guide" },
  { label: "Multi-AA Gateway API", to: "/mag/lsp-api" },
  { label: "Glossary", to: "/resources/glossary" },
];

export const ROLES = [
  {
    title: "Lenders",
    to: "/solutions/lenders",
    Icon: LandmarkIcon,
    summary:
      "Banks and NBFCs underwriting and monitoring loans on consented bank, GST and tax data.",
    links: [
      { label: "Multi-AA Gateway for lenders", to: "/mag/lender/overview" },
      { label: "Doc Parser", to: "/doc-parser/overview" },
    ],
  },
  {
    title: "Lending service providers",
    to: "/solutions/lsps",
    Icon: HandshakeIcon,
    summary:
      "One borrower journey that collects consent for many lenders, without the LSP touching the data.",
    links: [
      { label: "Explainer", to: "/mag/lsp/overview" },
      { label: "Integration guide", to: "/mag/lsp/integration-guide" },
      { label: "API reference", to: "/mag/lsp-api" },
    ],
  },
  {
    title: "Insurers",
    to: "/solutions/insurers",
    Icon: ShieldCheckIcon,
    summary:
      "Income verification for underwriting, consented financial data, and insurance data as an FIP.",
    links: [
      { label: "Digital ITR", to: "/digital-itr/overview" },
      { label: "Multi-AA Gateway for insurers", to: "/mag/insurer/overview" },
    ],
  },
  {
    title: "Wealth managers and AMCs",
    to: "/solutions/wealth",
    Icon: ChartPieIcon,
    summary:
      "Consolidated holdings across mutual funds, equities, NPS and deposits, for advice and engagement.",
    links: [
      { label: "Portfos", to: "/portfos/overview" },
      { label: "Multi-AA Gateway", to: "/mag/overview" },
    ],
  },
  {
    title: "Banks",
    to: "/solutions/banks",
    Icon: Building2Icon,
    summary:
      "Go live as an FIP, collect data as an FIU, and give customers a full view of their money.",
    links: [
      { label: "FIP Gateway", to: "/fip-gateway/overview" },
      { label: "Multi-AA Gateway", to: "/mag/overview" },
      { label: "Portfos", to: "/portfos/overview" },
    ],
  },
  {
    title: "Digital platforms",
    to: "/solutions/digital-platforms",
    Icon: SmartphoneIcon,
    summary:
      "Offer loans, insurance and investments in your app without becoming a financial institution.",
    links: [{ label: "OpenLink", to: "/openlink/overview" }],
  },
];

export const FLOW = [
  {
    title: "Data collection",
    body: "The customer consents once; data arrives from Account Aggregators, statements or the tax portal.",
    products: "Multi-AA Gateway · FIP Gateway · Doc Parser · Digital ITR",
  },
  {
    title: "Data intelligence",
    body: "Transactions are categorised and turned into profiles, holdings and insights.",
    products: "Portfos",
  },
  {
    title: "Decision intelligence",
    body: "Risk scores with reasons tell your teams whom to act on, and when.",
    products: "Coming soon",
  },
  {
    title: "Autonomous actions",
    body: "Intent becomes an offer and a fulfilled product, across platforms and providers.",
    products: "OpenLink",
  },
];
